/**
 * Broker Reconciliation PDF Generator
 *
 * Generates a professional PDF summary of the broker reconciliation:
 * - Loan information
 * - Gross check reconciliation with status
 * - Lender fees breakdown table
 * - Commission split (LO, Mentor, Company)
 */

import jsPDF from 'jspdf';

// Colors
const COLORS = {
  brandGreen: [22, 163, 74] as [number, number, number],
  black: [0, 0, 0] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  grayHeader: [220, 220, 220] as [number, number, number],
  grayText: [100, 100, 100] as [number, number, number],
  lightGray: [245, 245, 245] as [number, number, number],
  border: [180, 180, 180] as [number, number, number],
  green: [22, 163, 74] as [number, number, number],
  red: [220, 38, 38] as [number, number, number],
  violet: [124, 58, 237] as [number, number, number]
};

// Page dimensions (Letter size in points)
const PAGE = {
  width: 612,
  height: 792,
  marginLeft: 36,
  marginRight: 36,
  marginTop: 36,
  marginBottom: 50,
  get contentWidth() {
    return this.width - this.marginLeft - this.marginRight;
  }
};

export interface LenderFeeRow {
  name: string;
  totalFee: number;
  borrowerPAC: number;
  borrowerPOC: number;
  lenderPOC: number;
  reimbursement: number;
  reimburseTo: string;
}

export interface MentorPdfData {
  mentorName: string;
  splitPercent: number;
  commission: number;
  totalReimbursements: number;
  total: number;
  eAndO: number;
}

export interface BrokerReconPdfData {
  loanNumber: string;
  borrowerName: string;
  fundedDate: string | null;
  loanAmount: number;
  lenderName: string;
  loanOfficerName: string;
  processor: string;
  companyName: string;
  brokerSplit: number;
  grossCheckReceived: number;
  totalFees: number;
  checkVariance: number;
  isReconciled: boolean;
  lenderFees: LenderFeeRow[];
  totalBorrowerPAC: number;
  totalBorrowerPOC: number;
  totalLenderPOC: number;
  totalReimbursement: number;
  baseCheck: number;
  processingFee: number;
  eoInsurance: number;
  totalLOReimbursements: number;
  totalMentorReimbursements: number;
  netForSplit: number;
  loCommissionBase: number;
  loTotalCommission: number;
  loEAndO: number;
  companyCommissionBase: number;
  companyTotalRevenue: number;
  mentorData?: MentorPdfData;
  loTotalAfterMentor?: number;
}

// Helpers
function fmt(amount: number): string {
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(abs);
  return amount < 0 ? `(${formatted})` : formatted;
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) {
return '—';
}
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) {
return '—';
}
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function checkPageBreak(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PAGE.height - PAGE.marginBottom) {
    doc.addPage();
    return PAGE.marginTop;
  }
  return y;
}

function drawSectionHeader(doc: jsPDF, title: string, y: number, color?: [number, number, number]): number {
  const h = 16;
  doc.setFillColor(...(color || COLORS.grayHeader));
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, h, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...(color ? COLORS.white : COLORS.black));
  doc.text(title, PAGE.marginLeft + 5, y + 11);

  return y + h + 8;
}

// ─── PDF Sections ─────────────────────────────────────────

function drawHeader(doc: jsPDF, data: BrokerReconPdfData, y: number): number {
  doc.setFont('times', 'italic');
  doc.setFontSize(24);
  doc.setTextColor(...COLORS.black);
  doc.text('Lendwise', PAGE.marginLeft, y + 18);

  const bannerY = y + 30;
  doc.setFillColor(...COLORS.brandGreen);
  doc.rect(PAGE.marginLeft, bannerY, PAGE.contentWidth, 24, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.white);
  doc.text('Brokered Loan Reconciliation', PAGE.width / 2, bannerY + 16, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  doc.text(`${data.companyName}  |  Generated: ${today}`, PAGE.width - PAGE.marginRight, y + 18, { align: 'right' });

  return bannerY + 35;
}

function drawLoanDetails(doc: jsPDF, data: BrokerReconPdfData, startY: number): number {
  let y = drawSectionHeader(doc, 'Loan Details', startY);

  const c1 = PAGE.marginLeft + 5;
  const v1 = PAGE.marginLeft + 85;
  const c2 = PAGE.width / 2 + 10;
  const v2 = PAGE.width / 2 + 90;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.grayText);

  const rows: [string, string, string, string][] = [
    ['Loan Number:', data.loanNumber || '—', 'Funded Date:', fmtDate(data.fundedDate)],
    ['Borrower:', data.borrowerName || '—', 'Loan Amount:', fmt(data.loanAmount)],
    ['Lender:', data.lenderName, 'Loan Officer:', data.loanOfficerName],
    ['Processor:', data.processor || '—', 'Broker Split:', `${data.brokerSplit}%`]
  ];

  for (const [l1, val1, l2, val2] of rows) {
    doc.setTextColor(...COLORS.grayText);
    doc.text(l1, c1, y);
    doc.setTextColor(...COLORS.black);
    doc.text(val1, v1, y);
    doc.setTextColor(...COLORS.grayText);
    doc.text(l2, c2, y);
    doc.setTextColor(...COLORS.black);
    doc.text(val2, v2, y);
    y += 14;
  }

  return y + 4;
}

function drawReconBox(doc: jsPDF, data: BrokerReconPdfData, startY: number): number {
  const boxH = 52;
  const y = startY;

  const bgColor: [number, number, number] = data.isReconciled ? [220, 252, 231] : [254, 242, 242];
  doc.setFillColor(...bgColor);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.roundedRect(PAGE.marginLeft, y, PAGE.contentWidth, boxH, 4, 4, 'FD');

  const colW = PAGE.contentWidth / 3;
  const labelY = y + 14;
  const valY = y + 28;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  doc.text('Gross Check', PAGE.marginLeft + colW * 0.5, labelY, { align: 'center' });
  doc.text('Total Fees', PAGE.marginLeft + colW * 1.5, labelY, { align: 'center' });
  doc.text('Variance', PAGE.marginLeft + colW * 2.5, labelY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.black);
  doc.text(fmt(data.grossCheckReceived), PAGE.marginLeft + colW * 0.5, valY, { align: 'center' });
  doc.text(fmt(data.totalFees), PAGE.marginLeft + colW * 1.5, valY, { align: 'center' });

  const varColor = Math.abs(data.checkVariance) < 0.01 ? COLORS.green : COLORS.red;
  doc.setTextColor(...varColor);
  doc.text(fmt(data.checkVariance), PAGE.marginLeft + colW * 2.5, valY, { align: 'center' });

  doc.setFontSize(12);
  const statusColor = data.isReconciled ? COLORS.green : COLORS.red;
  doc.setTextColor(...statusColor);
  doc.text(data.isReconciled ? 'RECONCILED' : 'UNBALANCED', PAGE.width / 2, y + 44, { align: 'center' });

  return y + boxH + 12;
}

function drawFeesTable(doc: jsPDF, data: BrokerReconPdfData, startY: number): number {
  let y = drawSectionHeader(doc, 'Lender Fees', startY);

  const cols = [
    { label: 'Fee Name', x: PAGE.marginLeft, w: 120 },
    { label: 'Total Fee', x: PAGE.marginLeft + 120, w: 65 },
    { label: 'Borr PAC', x: PAGE.marginLeft + 185, w: 65 },
    { label: 'Borr POC', x: PAGE.marginLeft + 250, w: 65 },
    { label: 'Lender POC', x: PAGE.marginLeft + 315, w: 65 },
    { label: 'Reimburse', x: PAGE.marginLeft + 380, w: 65 },
    { label: 'Reimb To', x: PAGE.marginLeft + 445, w: 95 }
  ];

  // Header row helper (reused after page breaks)
  const headerH = 14;
  const drawFeeHeaders = (atY: number): number => {
    doc.setFillColor(...COLORS.lightGray);
    doc.setDrawColor(...COLORS.border);
    doc.rect(PAGE.marginLeft, atY, PAGE.contentWidth, headerH, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.black);
    for (const col of cols) {
      doc.text(col.label, col.x + 3, atY + 10);
    }
    return atY + headerH;
  };

  y = drawFeeHeaders(y);

  // Data rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const rowH = 13;

  for (let i = 0; i < data.lenderFees.length; i++) {
    const prevY = y;
    y = checkPageBreak(doc, y, rowH + 20);
    // Redraw column headers if we broke to a new page
    if (y < prevY) {
      y = drawFeeHeaders(y);
    }

    if (i % 2 === 0) {
      doc.setFillColor(...COLORS.lightGray);
      doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, rowH, 'F');
    }
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, rowH, 'S');

    const fee = data.lenderFees[i];
    doc.setTextColor(...COLORS.black);
    doc.text((fee.name || '').substring(0, 22), cols[0].x + 3, y + 9);
    doc.text(fmt(fee.totalFee), cols[1].x + 3, y + 9);
    doc.text(fmt(fee.borrowerPAC), cols[2].x + 3, y + 9);
    doc.text(fmt(fee.borrowerPOC), cols[3].x + 3, y + 9);
    doc.text(fmt(fee.lenderPOC), cols[4].x + 3, y + 9);
    doc.text(fmt(fee.reimbursement), cols[5].x + 3, y + 9);
    doc.text(fee.reimburseTo || '', cols[6].x + 3, y + 9);
    y += rowH;
  }

  // Totals row
  doc.setFillColor(...COLORS.grayHeader);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, rowH, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.text('TOTALS', cols[0].x + 3, y + 9);
  doc.text(fmt(data.totalFees), cols[1].x + 3, y + 9);
  doc.text(fmt(data.totalBorrowerPAC), cols[2].x + 3, y + 9);
  doc.text(fmt(data.totalBorrowerPOC), cols[3].x + 3, y + 9);
  doc.text(fmt(data.totalLenderPOC), cols[4].x + 3, y + 9);
  doc.text(fmt(data.totalReimbursement), cols[5].x + 3, y + 9);
  y += rowH;

  return y + 12;
}

function drawCommissionBreakdown(doc: jsPDF, data: BrokerReconPdfData, startY: number): number {
  let y = drawSectionHeader(doc, 'Commission Breakdown', startY);

  // Waterfall calculation
  const c1 = PAGE.marginLeft + 5;
  const v1 = PAGE.width - PAGE.marginRight - 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  // E&O is NOT a pre-split deduction — it is deducted from the LO/Mentor after the split (netDeduction
  // on each box) and collected by the company add-back. Listing it here too double-charges the LO side.
  const waterfall: [string, string, boolean?][] = [
    ['Base Check (Gross Check Received):', fmt(data.baseCheck)],
    ['Less Processing Fee:', `-${fmt(data.processingFee)}`],
    ['Less Reimbursements:', `-${fmt(data.totalLOReimbursements + data.totalMentorReimbursements)}`],
    ['Net for Commission Split:', fmt(data.netForSplit), true]
  ];

  for (const [label, value, bold] of waterfall) {
    doc.setTextColor(...COLORS.grayText);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(label, c1, y);
    doc.setTextColor(...COLORS.black);
    doc.text(value, v1, y, { align: 'right' });
    y += 13;
  }

  y += 6;
  y = checkPageBreak(doc, y, 120);

  // Commission boxes — all share the same top Y
  const hasMentor = !!data.mentorData;
  const boxCount = hasMentor ? 3 : 2;
  const boxGap = 10;
  const boxW = (PAGE.contentWidth - boxGap * (boxCount - 1)) / boxCount;
  const boxTopY = y;

  // LO Box
  const loBottom = drawCommissionBox(doc, {
    x: PAGE.marginLeft,
    y: boxTopY,
    w: boxW,
    title: `Loan Officer (${data.brokerSplit}%)`,
    rows: [
      ['Commission Base:', fmt(data.loCommissionBase)],
      ...(hasMentor ? [[`- Mentor Split (${data.mentorData!.splitPercent}%):`, `-${fmt(data.mentorData!.commission)}`] as [string, string]] : []),
      ...(data.totalLOReimbursements > 0 ? [['+ Reimbursements:', fmt(data.totalLOReimbursements)] as [string, string]] : [])
    ],
    grossLabel: 'Gross',
    grossValue: fmt(hasMentor && data.loTotalAfterMentor != null ? data.loTotalAfterMentor : data.loTotalCommission),
    netDeduction: ['Less E&O Insurance:', `-${fmt(data.loEAndO)}`],
    netValue: fmt((hasMentor && data.loTotalAfterMentor != null ? data.loTotalAfterMentor : data.loTotalCommission) - data.loEAndO),
    color: COLORS.green
  });

  let maxBottom = loBottom;

  // Mentor Box
  if (hasMentor) {
    const m = data.mentorData!;
    const mentorBottom = drawCommissionBox(doc, {
      x: PAGE.marginLeft + boxW + boxGap,
      y: boxTopY,
      w: boxW,
      title: `Mentor: ${m.mentorName}`,
      rows: [
        [`${m.splitPercent}% of LO Commission:`, fmt(m.commission)],
        ...(m.totalReimbursements > 0 ? [['+ Reimbursements:', fmt(m.totalReimbursements)] as [string, string]] : [])
      ],
      grossLabel: 'Gross',
      grossValue: fmt(m.total),
      netDeduction: ['Less E&O Insurance:', `-${fmt(m.eAndO)}`],
      netValue: fmt(m.total - m.eAndO),
      color: COLORS.violet
    });
    if (mentorBottom > maxBottom) {
maxBottom = mentorBottom;
}
  }

  // Company Box
  const companyBottom = drawCommissionBox(doc, {
    x: PAGE.marginLeft + (boxW + boxGap) * (boxCount - 1),
    y: boxTopY,
    w: boxW,
    title: `Company (${100 - data.brokerSplit}%)`,
    rows: [
      ['Commission Base:', fmt(data.companyCommissionBase)],
      ['+ Processing Fee:', fmt(data.processingFee)],
      ['+ E&O Insurance:', fmt(data.eoInsurance)]
    ],
    grossLabel: 'Total',
    grossValue: fmt(data.companyTotalRevenue),
    color: COLORS.brandGreen
  });
  if (companyBottom > maxBottom) {
maxBottom = companyBottom;
}

  return maxBottom + 8;
}

interface BoxOpts {
  x: number;
  y: number;
  w: number;
  title: string;
  rows: [string, string][];
  grossLabel: string;
  grossValue: string;
  netDeduction?: [string, string];
  netValue?: string;
  color: [number, number, number];
}

function drawCommissionBox(doc: jsPDF, opts: BoxOpts): number {
  const { x, y, w, title, rows, grossLabel, grossValue, netDeduction, netValue, color } = opts;
  const totalRows = rows.length + (netDeduction ? 2 : 0);
  const h = 30 + totalRows * 12 + 28;

  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, w, h, 3, 3, 'S');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...color);
  doc.text(title, x + 8, y + 14);

  // Rows
  let rowY = y + 28;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  for (const [label, val] of rows) {
    doc.setTextColor(...COLORS.grayText);
    doc.text(label, x + 8, rowY);
    doc.setTextColor(...COLORS.black);
    doc.text(val, x + w - 8, rowY, { align: 'right' });
    rowY += 12;
  }

  // Gross total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...color);
  doc.text(`${grossLabel}: ${grossValue}`, x + w - 8, rowY + 2, { align: 'right' });
  rowY += 14;

  // Net deduction
  if (netDeduction && netValue) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayText);
    doc.text(netDeduction[0], x + 8, rowY);
    doc.setTextColor(...COLORS.black);
    doc.text(netDeduction[1], x + w - 8, rowY, { align: 'right' });
    rowY += 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text(`Net: ${netValue}`, x + w - 8, rowY + 2, { align: 'right' });
    rowY += 14;
  }

  return y + h;
}

function addPageNumbers(doc: jsPDF): void {
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.grayText);
    doc.text(`Page ${i} of ${totalPages}`, PAGE.width / 2, PAGE.height - 25, { align: 'center' });
  }
}

// ─── Public API ───────────────────────────────────────────

export function generateBrokerReconPDF(data: BrokerReconPdfData): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });

  let y = PAGE.marginTop;

  y = drawHeader(doc, data, y);
  y = drawLoanDetails(doc, data, y);
  y = checkPageBreak(doc, y, 70);
  y = drawReconBox(doc, data, y);
  y = checkPageBreak(doc, y, 100);
  y = drawFeesTable(doc, data, y);
  y = checkPageBreak(doc, y, 200);
  y = drawCommissionBreakdown(doc, data, y);

  addPageNumbers(doc);
  return doc;
}

export function downloadBrokerReconPDF(data: BrokerReconPdfData): void {
  const doc = generateBrokerReconPDF(data);
  const loanNumber = data.loanNumber || 'Unknown';
  doc.save(`Broker_Recon_${loanNumber}.pdf`);
}
